import React, { useState, useMemo } from 'react';
import { UserData, Plan, UserType, DailyBucket } from '../types';

interface UserTableProps {
  users: UserData[];
  onUserUpdate: (email: string, updates: Partial<UserData>) => Promise<void>;
  onUserDelete: (email: string) => Promise<void>;
}

type SortField = 'email' | 'name' | 'userType' | 'messages' | 'discoveries' | 'gists' | 'citationClicks' | 'panelOpens' | 'plan' | 'lastMessageAt' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const PLAN_OPTIONS: (Plan | '')[] = ['', 'essential', 'professional', 'starter', 'scholar'];
const USER_TYPE_OPTIONS: (UserType | '')[] = ['', 'law_student', 'paralegal', 'lawyer', 'researcher', 'other'];

type PlanFilter = 'all' | 'trial' | Plan;
type TypeFilter = 'all' | UserType;
type ActivityFilter = 'all' | 'active' | 'inactive';

function sumDailyField(user: UserData, ...fields: (keyof DailyBucket)[]): number {
  if (!user.dailyActivity) return 0;
  let total = 0;
  for (const bucket of Object.values(user.dailyActivity)) {
    const b = bucket as Record<string, number>;
    for (const f of fields) {
      total += b[f] || 0;
    }
  }
  return total;
}

export function UserTable({ users, onUserUpdate, onUserDelete }: UserTableProps) {
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filter, setFilter] = useState('');
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserData>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getLastActiveDate = (user: UserData): string | undefined => {
    if (user.lastMessageAt) return user.lastMessageAt;
    if (user.dailyActivity) {
      const dates = Object.keys(user.dailyActivity).sort();
      if (dates.length > 0) return dates[dates.length - 1];
    }
    return undefined;
  };

  const filteredUsers = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return users.filter(user => {
      if (filter) {
        const q = filter.toLowerCase();
        if (!user.email.toLowerCase().includes(q) && !(user.name?.toLowerCase().includes(q))) {
          return false;
        }
      }
      if (planFilter !== 'all') {
        if (planFilter === 'trial') {
          if (user.subscribed) return false;
        } else {
          if (user.plan !== planFilter) return false;
        }
      }
      if (typeFilter !== 'all') {
        if (user.userType !== typeFilter) return false;
      }
      if (activityFilter !== 'all') {
        const lastActive = getLastActiveDate(user);
        const activeRecently = lastActive && new Date(lastActive) >= sevenDaysAgo;
        if (activityFilter === 'active' && !activeRecently) return false;
        if (activityFilter === 'inactive' && activeRecently) return false;
      }
      return true;
    });
  }, [users, filter, planFilter, typeFilter, activityFilter]);

  const getValue = (user: UserData, field: SortField): string | number | boolean => {
    if (field === 'messages') return user.messageCount || 0;
    if (field === 'discoveries') return user.similarCasesCount || 0;
    if (field === 'gists') return sumDailyField(user, 'g');
    if (field === 'citationClicks') return sumDailyField(user, 'cc');
    if (field === 'panelOpens') return sumDailyField(user, 'tp');
    if (field === 'lastMessageAt') return getLastActiveDate(user) ?? '';
    return user[field as keyof UserData] as string ?? '';
  };

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aVal = getValue(a, sortField);
      const bVal = getValue(b, sortField);

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return sortDirection === 'asc'
          ? (aVal ? 1 : 0) - (bVal ? 1 : 0)
          : (bVal ? 1 : 0) - (aVal ? 1 : 0);
      }

      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [filteredUsers, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatUserType = (type?: string) => {
    if (!type) return '-';
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const openEditModal = (user: UserData) => {
    setEditingUser(user);
    setEditForm({
      plan: user.plan,
      messageCount: user.messageCount,
      similarCasesCount: user.similarCasesCount,
      name: user.name || '',
      userType: user.userType,
      affiliate: user.affiliate,
    });
    setSaveError(null);
    setConfirmingDelete(false);
  };

  const handleDelete = async () => {
    if (!editingUser) return;
    setDeleting(true);
    setSaveError(null);
    try {
      await onUserDelete(editingUser.email);
      setEditingUser(null);
      setConfirmingDelete(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updates = {
        ...editForm,
        subscribed: !!editForm.plan,
      };
      await onUserUpdate(editingUser.email, updates);
      setEditingUser(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <th onClick={() => handleSort(field)} className={sortField === field ? 'sorted' : ''}>
      {children}
      {sortField === field && (
        <span className="sort-arrow">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
      )}
    </th>
  );

  return (
    <div className="user-table-container">
      <div className="table-header">
        <h2>Users ({sortedUsers.length}{sortedUsers.length !== users.length ? ` / ${users.length}` : ''})</h2>
        <div className="table-filters">
          <input
            type="text"
            placeholder="Search email or name..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-input"
          />
          <select
            className="filter-select"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value as PlanFilter)}
          >
            <option value="all">All Plans</option>
            <option value="trial">Free Trial</option>
            <option value="essential">Essential</option>
            <option value="professional">Professional</option>
            <option value="starter">Starter</option>
            <option value="scholar">Scholar</option>
          </select>
          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            <option value="all">All Types</option>
            <option value="lawyer">Lawyer</option>
            <option value="law_student">Law Student</option>
            <option value="paralegal">Paralegal</option>
            <option value="researcher">Researcher</option>
            <option value="other">Other</option>
          </select>
          <select
            className="filter-select"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value as ActivityFilter)}
          >
            <option value="all">All Activity</option>
            <option value="active">Active (7d)</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="table-scroll">
        <table className="user-table">
          <thead>
            <tr>
              <th>#</th>
              <SortHeader field="email">Email</SortHeader>
              <SortHeader field="name">Name</SortHeader>
              <SortHeader field="userType">Type</SortHeader>
              <SortHeader field="messages">Msgs</SortHeader>
              <SortHeader field="discoveries">Disc</SortHeader>
              <SortHeader field="gists">Gists</SortHeader>
              <SortHeader field="citationClicks">Cites</SortHeader>
              <SortHeader field="panelOpens">Panel</SortHeader>
              <SortHeader field="plan">Plan</SortHeader>
              <SortHeader field="lastMessageAt">Last Active</SortHeader>
              <SortHeader field="createdAt">Created</SortHeader>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user, i) => (
              <tr key={user.email} onClick={() => openEditModal(user)} className="clickable-row">
                <td style={{ color: '#9ca3af' }}>{sortedUsers.length - i}</td>
                <td>{user.affiliate && <span className="affiliate-badge" title="Affiliate">★</span>}{user.email}</td>
                <td>{user.name || '-'}</td>
                <td>{formatUserType(user.userType)}</td>
                <td>{user.messageCount || 0}</td>
                <td>{user.similarCasesCount || 0}</td>
                <td>{sumDailyField(user, 'g')}</td>
                <td>{sumDailyField(user, 'cc')}</td>
                <td>{sumDailyField(user, 'tp')}</td>
                <td className={`plan-${user.plan || 'trial'}`}>
                  {user.plan || 'trial'}
                  {user.cancelledAt && <span className="churned-badge" title="Cancelled">cancelling</span>}
                  {!user.subscribed && user.stripeCustomerId && !user.cancelledAt && <span className="churned-badge" title="Churned">churned</span>}
                </td>
                <td>{formatDate(getLastActiveDate(user))}</td>
                <td>{formatDate(user.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Edit User</h3>
            <p className="modal-email">{editingUser.email}</p>

            {saveError && <div className="modal-error">{saveError}</div>}

            <div className="modal-field">
              <label>Name</label>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className="modal-field">
              <label>User Type</label>
              <select
                value={editForm.userType || ''}
                onChange={(e) => setEditForm(f => ({ ...f, userType: (e.target.value || undefined) as UserType | undefined }))}
              >
                {USER_TYPE_OPTIONS.map(t => (
                  <option key={t} value={t}>{t ? formatUserType(t) : '(none)'}</option>
                ))}
              </select>
            </div>

            <div className="modal-field">
              <label>Plan</label>
              <select
                value={editForm.plan || ''}
                onChange={(e) => setEditForm(f => ({ ...f, plan: (e.target.value || undefined) as Plan | undefined }))}
              >
                {PLAN_OPTIONS.map(p => (
                  <option key={p} value={p}>{p || '(none / trial)'}</option>
                ))}
              </select>
            </div>

            <div className="modal-status">
              <span className={editForm.plan ? 'status-active' : 'status-trial'}>
                {editForm.plan ? 'Active subscription' : 'Free trial'}
              </span>
              {editingUser.studentVerified && <span className="status-student">Student verified</span>}
              {editForm.affiliate && <span className="status-affiliate">Affiliate</span>}
            </div>

            <div className="modal-field">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.affiliate || false}
                  onChange={(e) => setEditForm(f => ({ ...f, affiliate: e.target.checked }))}
                />
                Affiliate (exclude from revenue metrics)
              </label>
            </div>

            <div className="modal-row">
              <div className="modal-field">
                <label>Messages</label>
                <input
                  type="number"
                  min={0}
                  value={editForm.messageCount ?? 0}
                  onChange={(e) => setEditForm(f => ({ ...f, messageCount: parseInt(e.target.value, 10) || 0 }))}
                />
              </div>

              <div className="modal-field">
                <label>Discoveries</label>
                <input
                  type="number"
                  min={0}
                  value={editForm.similarCasesCount ?? 0}
                  onChange={(e) => setEditForm(f => ({ ...f, similarCasesCount: parseInt(e.target.value, 10) || 0 }))}
                />
              </div>
            </div>

            <div className="modal-info">
              <span>Created: {formatDate(editingUser.createdAt)}</span>
              <span>Last active: {formatDate(getLastActiveDate(editingUser))}</span>
              {editingUser.stripeCustomerId && <span>Stripe: {editingUser.stripeCustomerId}</span>}
            </div>

            <div className="modal-actions">
              {confirmingDelete ? (
                <>
                  <span className="delete-confirm-text">Are you sure? This cannot be undone.</span>
                  <button className="modal-cancel" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                    No, keep
                  </button>
                  <button className="modal-delete" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Yes, delete'}
                  </button>
                </>
              ) : (
                <>
                  <button className="modal-delete-trigger" onClick={() => setConfirmingDelete(true)}>
                    Delete account
                  </button>
                  <div className="modal-actions-right">
                    <button className="modal-cancel" onClick={() => setEditingUser(null)}>Cancel</button>
                    <button className="modal-save" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
